export interface ValidationResult {
    isValid: boolean;
    error?: string;
}

export const validateDisplayName = (name: string): ValidationResult => {
    if (!name.trim()) {
        return { isValid: false, error: 'Display name is required' };
    }
    if (name.length > 30) {
        return { isValid: false, error: 'Too many characters!' };
    }
    return { isValid: true };
};

export const validateUrl = (url: string): ValidationResult => {
    if (!url) return { isValid: true }; // Empty URLs are valid (optional fields)

    try {
        new URL(url);
        return { isValid: true };
    } catch {
        return { isValid: false, error: 'Please enter a valid URL' };
    }
};

export const isValidUrl = (url: string): boolean => {
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
};

export const validateSocialMediaHandle = (input: string, platform: string): ValidationResult => {
    if (!input) return { isValid: true }; // Empty inputs are valid (optional fields)

    // Check if it's a full URL first
    if (isValidUrl(input)) {
        switch (platform) {
        case 'instagram':
            if (input.includes('instagram.com/')) return { isValid: true };
            break;
        case 'facebook':
            if (input.includes('facebook.com/')) return { isValid: true };
            break;
        case 'youtube':
            if (input.includes('youtube.com/') || input.includes('youtu.be/')) return { isValid: true };
            break;
        case 'tiktok':
            if (input.includes('tiktok.com/')) return { isValid: true };
            break;
        case 'snapchat':
            if (input.includes('snapchat.com/')) return { isValid: true };
            break;
        }
        return { isValid: false, error: `Please enter a valid ${platform} link` };
    }

    // If not a URL, validate as handle
    let handle = input;
    // Remove @ symbol if present
    if (handle.startsWith('@')) {
        handle = handle.substring(1);
    }

    // Platform-specific validation rules for handles
    switch (platform) {
    case 'instagram':
        if (!/^[a-zA-Z0-9._]{1,30}$/.test(handle)) {
            return {
                isValid: false,
                error: 'Invalid Instagram username or profile link'
            };
        }
        break;

    case 'facebook':
        if (!/^[a-zA-Z0-9.]{5,50}$/.test(handle)) {
            return {
                isValid: false,
                error: 'Invalid Facebook username or profile link'
            };
        }
        break;

    case 'youtube':
        if (!/^[a-zA-Z0-9_-]{3,30}$/.test(handle)) {
            return {
                isValid: false,
                error: 'Invalid YouTube username, enter @channel or channel link'
            };
        }
        break;

    case 'tiktok':
        // Allow both username format and tiktok.com/@username format
        if (!/^[a-zA-Z0-9._]{2,24}$/.test(handle) && !handle.includes('tiktok.com/')) {
            return {
                isValid: false,
                error: 'Invalid TikTok username. Enter a valid handle (e.g., @username) or profile link'
            };
        }
        break;

    case 'snapchat':
        if (!/^[a-zA-Z0-9._-]{3,15}$/.test(handle)) {
            return {
                isValid: false,
                error: 'Invalid Snapchat username. Enter a valid username or profile link'
            };
        }
        break;
    }

    return { isValid: true };
};

export const validateSpotifyArtistUrl = (url: string): ValidationResult => {
    if (!url) return { isValid: true };

    // Allow both full URLs and Spotify artist IDs
    if (url.includes('spotify.com/artist/')) {
        return { isValid: true };
    }

    // If it's not a URL, check if it might be just the artist ID
    if (url.startsWith('spotify:artist:') || /^[a-zA-Z0-9]{22}$/.test(url)) {
        return { isValid: true };
    }

    return {
        isValid: false,
        error: 'Invalid Spotify artist link e.g. https://open.spotify.com/artist/...'
    };
};

export const validateAppleMusicArtistUrl = (url: string): ValidationResult => {
    if (!url) return { isValid: true };

    if (url.includes('music.apple.com/')) {
        return { isValid: true };
    }

    return {
        isValid: false,
        error: 'Invalid Apple Music artist link'
    };
};

export const validateSoundCloudUrl = (url: string): ValidationResult => {
    if (!url) return { isValid: true };

    // Accept both profile URLs and track/playlist URLs
    if (url.includes('soundcloud.com/')) {
        return { isValid: true };
    }

    // If it's just a username without the URL
    if (/^[a-zA-Z0-9-_]{3,25}$/.test(url)) {
        return { isValid: true };
    }

    return {
        isValid: false,
        error: 'Invalid SoundCloud profile link'
    };
};

export const validateBandcampUrl = (url: string): ValidationResult => {
    if (!url) return { isValid: true };

    // Accept both artist.bandcamp.com and bandcamp.com/artist formats
    if (url.includes('bandcamp.com/') || url.includes('.bandcamp.com')) {
        return { isValid: true };
    }

    // If it's just a domain without protocol, try adding https://
    if (url.includes('.bandcamp.com') && !url.startsWith('http')) {
        return { isValid: true };
    }

    return {
        isValid: false,
        error: 'Enter a Bandcamp artist URL (e.g., artist.bandcamp.com or bandcamp.com/artist)'
    };
};

/** Hostname must include a DNS label exactly named `discord` (e.g. discord.com, discord.gg) or legacy discordapp.com */
function isDiscordHostname(hostname: string): boolean {
    const h = hostname.toLowerCase();
    const labels = h.split('.');
    if (labels.includes('discord')) return true;
    return h === 'discordapp.com' || h.endsWith('.discordapp.com');
}

export const validateDiscordUrl = (url: string): ValidationResult => {
    if (!url) return { isValid: true };
    const trimmed = url.trim();
    let toParse = trimmed;
    if (!/^https?:\/\//i.test(toParse)) {
        toParse = `https://${toParse}`;
    }
    try {
        const { hostname } = new URL(toParse);
        if (isDiscordHostname(hostname)) {
            return { isValid: true };
        }
    } catch {
        return {
            isValid: false,
            error: 'Enter a valid Discord link (e.g. https://discord.com/... or https://discord.gg/...)'
        };
    }
    return {
        isValid: false,
        error: 'Enter a valid Discord link (e.g. https://discord.com/... or https://discord.gg/...)'
    };
};

function isTwitchHostname(hostname: string): boolean {
    const labels = hostname.toLowerCase().split('.');
    return labels.includes('twitch');
}

export const validateTwitchUrl = (url: string): ValidationResult => {
    if (!url) return { isValid: true };
    const trimmed = url.trim();
    let toParse = trimmed;
    if (!/^https?:\/\//i.test(toParse)) {
        toParse = `https://${toParse}`;
    }
    try {
        const { hostname } = new URL(toParse);
        if (isTwitchHostname(hostname)) {
            return { isValid: true };
        }
    } catch {
        return {
            isValid: false,
            error: 'Enter a valid Twitch link (e.g. https://www.twitch.tv/...)'
        };
    }
    return {
        isValid: false,
        error: 'Enter a valid Twitch link (e.g. https://www.twitch.tv/...)'
    };
};

/** X (Twitter): x.com, twitter.com, t.co, etc. */
function isXHostname(hostname: string): boolean {
    const h = hostname.toLowerCase();
    if (h === 't.co') return true;
    const labels = h.split('.');
    if (labels.includes('twitter')) return true;
    if (labels.includes('x') && (h === 'x.com' || h.endsWith('.x.com'))) return true;
    return false;
}

export const validateXUrl = (url: string): ValidationResult => {
    if (!url) return { isValid: true };
    const trimmed = url.trim();
    let toParse = trimmed;
    if (!/^https?:\/\//i.test(toParse)) {
        toParse = `https://${toParse}`;
    }
    try {
        const { hostname } = new URL(toParse);
        if (isXHostname(hostname)) {
            return { isValid: true };
        }
    } catch {
        return {
            isValid: false,
            error: 'Enter a valid X link (e.g. https://x.com/... or https://twitter.com/...)'
        };
    }
    return {
        isValid: false,
        error: 'Enter a valid X link (e.g. https://x.com/... or https://twitter.com/...)'
    };
};

function isLinkedInHostname(hostname: string): boolean {
    const h = hostname.toLowerCase();
    const labels = h.split('.');
    if (labels.includes('linkedin')) return true;
    if (h === 'lnkd.in' || h.endsWith('.lnkd.in')) return true;
    return false;
}

export const validateLinkedInUrl = (url: string): ValidationResult => {
    if (!url) return { isValid: true };
    const trimmed = url.trim();
    let toParse = trimmed;
    if (!/^https?:\/\//i.test(toParse)) {
        toParse = `https://${toParse}`;
    }
    try {
        const { hostname } = new URL(toParse);
        if (isLinkedInHostname(hostname)) {
            return { isValid: true };
        }
    } catch {
        return {
            isValid: false,
            error: 'Enter a valid LinkedIn link (e.g. https://www.linkedin.com/in/...)'
        };
    }
    return {
        isValid: false,
        error: 'Enter a valid LinkedIn link (e.g. https://www.linkedin.com/in/...)'
    };
};

/** Official Threads host: threads.net */
function isThreadsHostname(hostname: string): boolean {
    const h = hostname.toLowerCase();
    return h === 'threads.net' || h.endsWith('.threads.net');
}

export const validateThreadsUrl = (url: string): ValidationResult => {
    if (!url) return { isValid: true };
    const trimmed = url.trim();
    let toParse = trimmed;
    if (!/^https?:\/\//i.test(toParse)) {
        toParse = `https://${toParse}`;
    }
    try {
        const { hostname } = new URL(toParse);
        if (isThreadsHostname(hostname)) {
            return { isValid: true };
        }
    } catch {
        return {
            isValid: false,
            error: 'Enter a valid Threads link (e.g. https://www.threads.net/@...)'
        };
    }
    return {
        isValid: false,
        error: 'Enter a valid Threads link (e.g. https://www.threads.net/@...)'
    };
};

function isNewgroundsHostname(hostname: string): boolean {
    const h = hostname.toLowerCase();
    return h === 'newgrounds.com' || h.endsWith('.newgrounds.com');
}

export const validateNewgroundsUrl = (url: string): ValidationResult => {
    if (!url) return { isValid: true };
    const trimmed = url.trim();
    let toParse = trimmed;
    if (!/^https?:\/\//i.test(toParse)) {
        toParse = `https://${toParse}`;
    }
    try {
        const { hostname } = new URL(toParse);
        if (isNewgroundsHostname(hostname)) {
            return { isValid: true };
        }
    } catch {
        return {
            isValid: false,
            error: 'Enter a valid Newgrounds link (e.g. https://user.newgrounds.com/...)'
        };
    }
    return {
        isValid: false,
        error: 'Enter a valid Newgrounds link (e.g. https://user.newgrounds.com/...)'
    };
};
